import { authedGet } from '../../api/http';
import { buildQuery } from '../../api/query';
import type { CatalogSearchResponse } from '../../data/catalogSearch';
import type { PublishedPrices } from '../../lib/contentPrice';
import type { RawCatalogEntry } from '../../lib/catalogMap';
import { platformIdOf } from '../../lib/catalogPlatform';
import { validateCatalogAuthority } from './editorial';
import { readDiscoveryList, readDiscoverySearch } from './discoveryReader';
import { prerenderInput } from '../prerender/buildInput';

export const CATALOG_FRESH_TIME = 5 * 60 * 1_000;

export interface CatalogSearchOptions {
  category?: string;
  platform?: string;
  limit?: number;
}

/** Fetches the sole authoritative catalog snapshot used by all projections. */
export const fetchCatalogEntries = async (): Promise<RawCatalogEntry[]> => {
  const buildInput = prerenderInput('catalog');
  const wire = buildInput ?? await authedGet<unknown>('/catalog');
  const entries = readDiscoveryList(wire);
  validateCatalogAuthority(entries);
  return entries;
};

/**
 * The catalog reduced to each SKU's published pricing, for pages that quote
 * prices and show nothing else of it. Measured on 2026-09-27: 64 KB of JSON
 * against 574 KB for the full catalog, and whatever a loader fetches is
 * dehydrated into that page's HTML.
 */
export const fetchPublishedPrices = async (): Promise<PublishedPrices> =>
  Object.fromEntries((await fetchCatalogEntries()).map((entry) => [entry.slug, entry.pricing]));

export const searchCatalogRanked = async (
  query: string,
  options?: CatalogSearchOptions,
): Promise<CatalogSearchResponse> => {
  const querystring = buildQuery({
    q: query,
    category: options?.category,
    platform: options?.platform,
    limit: options?.limit,
  });
  return readDiscoverySearch(await authedGet<unknown>(`/catalog/search${querystring}`));
};

export const searchCatalogIds = async (query: string): Promise<string[]> => {
  const response = await searchCatalogRanked(query);
  const ids: string[] = [];
  for (const entry of response.results) {
    const id = platformIdOf(entry.slug);
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
};
